import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Anchor, Button, Code, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import {
  useCallableContext,
  useCompositionScope,
} from '@/hooks/api/automations/useExecutionCompositions.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import {
  create_automation_version_test_case_plan_url,
  create_automation_version_test_case_run_from_plan_url,
  get_automation_version_test_case_runs_url,
} from '@/utils/urls.ts';
import type { AutomationVersionTestCaseRun } from '@/types/automations.ts';
import type { BoundRequirement, Identity, EvidenceBinding } from '@/types/executionCompositions.ts';
import { ExactDetails } from './CompositionPresentation.tsx';
import CompositionRunLink from './CompositionRunLink.tsx';

export type ExactPlan = {
  eligible: boolean;
  plan_token?: string | null;
  expires_at?: string | null;
  execution_slice?: { scheduled_step_ids: string[]; blocked_effect_step_ids: string[] } | null;
  issues?: { reason_code: string; message: string }[];
  automation_release_test?: {
    schema_version: string;
    requirement_id: string;
    evidence: EvidenceBinding;
    resource_digest: string;
    requirements_digest: string;
    execution: { release_id: string; composition_digest: string; current: { binding_id: string } };
  } | null;
};
type Launch = { request_id: string; plan_token: string; run_id?: string };
function readLaunch(key: string): Launch | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) ?? 'null') as Launch | null;
    return value && typeof value.request_id === 'string' && typeof value.plan_token === 'string'
      ? value
      : null;
  } catch {
    return null;
  }
}
function retainLaunch(key: string, launch: Launch | null) {
  try {
    if (launch) sessionStorage.setItem(key, JSON.stringify(launch));
    else sessionStorage.removeItem(key);
  } catch {
    /* In-memory recovery remains available. */
  }
}

export default function ExactCompositionTest(props: {
  releaseId: string;
  requirement: BoundRequirement;
  identity: Identity;
  onEvidenceChange: () => void;
  compact?: boolean;
}) {
  const scope = useCompositionScope();
  return (
    <ExactTestSession
      key={JSON.stringify([...scope, props.releaseId, props.requirement, props.identity])}
      {...props}
      storageKey={JSON.stringify([
        ...scope,
        'test-launch',
        props.releaseId,
        props.requirement,
        props.identity,
      ])}
    />
  );
}
function ExactTestSession({
  releaseId,
  requirement,
  identity,
  onEvidenceChange,
  storageKey,
  compact = false,
}: {
  releaseId: string;
  requirement: BoundRequirement;
  identity: Identity;
  onEvidenceChange: () => void;
  storageKey: string;
  compact?: boolean;
}) {
  const api = useApiClient();
  const source = useCallableContext(requirement.declaration.evidence.automation_version_id);
  const [plan, setPlan] = useState<ExactPlan | null>(null);
  const [launch, setLaunch] = useState<Launch | null>(() => readLaunch(storageKey));
  const [producer, setProducer] = useState('');
  const [replayRun, setReplayRun] = useState('');
  const [replaySelection, setReplaySelection] = useState('original');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [runId, setRunId] = useState('');
  const [rejected, setRejected] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const history = useQuery({
    queryKey: [storageKey, 'version-case-history'],
    enabled: showHistory,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async ({ signal }) => {
      const { data } = await api.get<{ runs: AutomationVersionTestCaseRun[] }>(
        get_automation_version_test_case_runs_url(
          requirement.declaration.evidence.automation_version_id,
          requirement.declaration.evidence.case_id,
        ),
        { signal },
      );
      if (
        !Array.isArray(data.runs) ||
        data.runs.some(
          (run) =>
            !run ||
            typeof run.run_id !== 'string' ||
            !run.run_id.trim() ||
            typeof run.run_status !== 'string',
        )
      )
        throw new Error('Run history could not be confirmed.');
      return data;
    },
  });
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const hasPreparedCompactPlan = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const declaration = requirement.declaration;
  const expiry = plan?.expires_at ? Date.parse(plan.expires_at) : NaN;
  const expired = !Number.isFinite(expiry) || expiry <= Date.now();
  const [, tick] = useState(0);
  useEffect(() => {
    if (!plan?.expires_at) return;
    const timer = setTimeout(
      () => tick((value) => value + 1),
      Math.max(0, Date.parse(plan.expires_at) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [plan]);
  const prepare = useCallback(async () => {
    if (
      inFlight.current ||
      launch ||
      source.isError ||
      !source.isFetchedAfterMount ||
      !source.data?.latest_version_id
    )
      return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    setPlan(null);
    try {
      const { data } = await api.post<ExactPlan>(
        create_automation_version_test_case_plan_url(
          source.data.automation_id,
          source.data.automation_version_id,
          declaration.evidence.case_id,
        ),
        {
          expected_latest_version_id: source.data.latest_version_id,
          expected_published_version_id: source.data.published_version_id,
          expected_head_revision: source.data.head_revision,
          automation_release_id: releaseId,
          composition_requirement_id: declaration.requirement_id,
          ...(declaration.kind === 'comparison' ? { producer_run_id: producer } : {}),
          ...(replayRun
            ? { composition_replay: { source_run_id: replayRun, selection: replaySelection } }
            : {}),
        },
      );
      const association = data.automation_release_test;
      if (
        data.eligible &&
        (!association ||
          association.schema_version !== 'automation_release_test.v3' ||
          association.execution.release_id !== releaseId ||
          association.execution.composition_digest !== identity.composition_digest ||
          association.requirements_digest !== identity.requirements_digest ||
          (declaration.kind !== 'component' &&
            association.resource_digest !== identity.resource_digest) ||
          association.evidence.suite_digest !== declaration.evidence.suite_digest ||
          association.evidence.case_digest !== declaration.evidence.case_digest ||
          association.evidence.assertion_suite_sha256 !==
            declaration.evidence.assertion_suite_sha256 ||
          [...association.evidence.assertion_ids].sort().join('\0') !==
            [...declaration.evidence.assertion_ids].sort().join('\0') ||
          association.requirement_id !== declaration.requirement_id ||
          association.execution.current.binding_id !== requirement.evidence_binding_id ||
          association.evidence.case_id !== declaration.evidence.case_id ||
          association.evidence.automation_version_id !== declaration.evidence.automation_version_id)
      )
        throw new Error('Test plan does not confirm the selected composition and requirement.');
      if (mounted.current) setPlan(data);
    } catch (cause) {
      if (mounted.current)
        setError(
          getHttpErrorMessage(cause) ||
            (cause instanceof Error ? cause.message : 'Could not prepare test.'),
        );
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  }, [
    api,
    declaration,
    identity.composition_digest,
    identity.requirements_digest,
    identity.resource_digest,
    launch,
    producer,
    releaseId,
    replayRun,
    replaySelection,
    requirement.evidence_binding_id,
    source,
  ]);
  useEffect(() => {
    if (
      !compact ||
      hasPreparedCompactPlan.current ||
      busy ||
      plan ||
      launch ||
      source.isError ||
      !source.isFetchedAfterMount ||
      !source.data?.latest_version_id
    )
      return;
    hasPreparedCompactPlan.current = true;
    void prepare();
  }, [
    compact,
    busy,
    launch,
    plan,
    prepare,
    source.data?.latest_version_id,
    source.isError,
    source.isFetchedAfterMount,
  ]);
  const run = async () => {
    if (
      inFlight.current ||
      !source.data ||
      (!launch && (!plan?.eligible || !plan.plan_token || expired))
    )
      return;
    const pending = launch ?? { request_id: crypto.randomUUID(), plan_token: plan!.plan_token! };
    setShowHistory(false);
    retainLaunch(storageKey, pending);
    setLaunch(pending);
    inFlight.current = true;
    setBusy(true);
    setError('');
    setRejected(false);
    try {
      const { data } = await api.post<{ id?: string; liveness?: { reason_code: string } }>(
        create_automation_version_test_case_run_from_plan_url(
          source.data.automation_id,
          source.data.automation_version_id,
          declaration.evidence.case_id,
        ),
        { plan_token: pending.plan_token, request_id: pending.request_id, confirm_run: true },
      );
      if (
        !data.id ||
        !data.liveness ||
        data.liveness.reason_code === 'builder_test_launch_preparation_unobserved'
      ) {
        const uncertain = { ...pending, run_id: data.id };
        retainLaunch(storageKey, uncertain);
        if (mounted.current) {
          setLaunch(uncertain);
          setError('Launch is unconfirmed. Retry the same request before preparing another test.');
        }
      } else {
        retainLaunch(storageKey, null);
        if (mounted.current) {
          setLaunch(null);
          setPlan(null);
          setRunId(data.id);
          onEvidenceChange();
        }
      }
    } catch (cause) {
      if (axios.isAxiosError(cause) && cause.response?.status === 403)
        retainLaunch(storageKey, null);
      if (mounted.current) {
        if (
          axios.isAxiosError(cause) &&
          cause.response &&
          cause.response.status >= 400 &&
          cause.response.status < 500
        )
          setRejected(true);
        if (axios.isAxiosError(cause) && cause.response?.status === 403) {
          setLaunch(null);
          setPlan(null);
        }
        setError(getHttpErrorMessage(cause) || 'Could not confirm launch. Retry the same request.');
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const invalidate = () => {
    setPlan(null);
    setError('');
  };
  return (
    <Stack gap="xs">
      {!compact && declaration.kind === 'comparison' && (
        <TextInput
          label="Actual artifact producer Run"
          value={producer}
          disabled={busy || Boolean(launch)}
          onChange={(event) => {
            setProducer(event.currentTarget.value);
            invalidate();
          }}
          description="The server verifies artifact lineage and baseline eligibility."
        />
      )}
      {!compact && (
        <TextInput
          label="Replay source Run (optional)"
          value={replayRun}
          disabled={busy || Boolean(launch)}
          onChange={(event) => {
            setReplayRun(event.currentTarget.value);
            invalidate();
          }}
        />
      )}
      {!compact && replayRun && (
        <Select
          label="Replay composition"
          value={replaySelection}
          disabled={busy || Boolean(launch)}
          data={[
            { value: 'original', label: 'Original captured composition' },
            { value: 'selected', label: 'Selected candidate composition' },
          ]}
          onChange={(value) => {
            setReplaySelection(value ?? 'original');
            invalidate();
          }}
        />
      )}
      {source.isError && (
        <Alert color="red">
          Exact test source is unavailable.{' '}
          <Anchor onClick={() => void source.refetch()}>Refresh source</Anchor>
        </Alert>
      )}
      {error && <Alert color="red">{error}</Alert>}
      {plan && (
        <Alert
          color={plan.eligible ? 'blue' : 'orange'}
          title={
            plan.eligible
              ? compact
                ? 'Test ready'
                : 'Review exact safe-test plan'
              : compact
                ? 'Test blocked'
                : 'Test planning blocked'
          }
        >
          {!compact && (
            <Text>
              {plan.execution_slice?.scheduled_step_ids.length ?? 0} scheduled steps ·{' '}
              {plan.execution_slice?.blocked_effect_step_ids.length ?? 0} blocked effect steps
            </Text>
          )}
          {!compact && (
            <ExactDetails
              summary="Exact test selection"
              values={{
                'Candidate ID': releaseId,
                'Binding ID': requirement.evidence_binding_id,
              }}
            />
          )}
          {plan.issues?.map((issue, index) => (
            <Text key={index}>{issue.message}</Text>
          ))}
          {expired && <Text>Plan expired. Prepare and review a fresh plan.</Text>}
        </Alert>
      )}
      {launch && (
        <Alert color="orange">
          {compact ? (
            'The test launch needs confirmation before another test can be prepared.'
          ) : (
            <>
              Pending request <Code>{launch.request_id}</Code>. Resolve this request before a new
              plan.
            </>
          )}
          {launch.run_id && (
            <CompositionRunLink runId={launch.run_id}> Inspect possible Run</CompositionRunLink>
          )}
        </Alert>
      )}
      {launch && !compact && (
        <Button
          variant="subtle"
          onClick={() => {
            setShowHistory(true);
            if (showHistory) void history.refetch();
          }}
        >
          Inspect version-case Run history
        </Button>
      )}
      {showHistory && !compact && (
        <Stack gap="xs">
          <Text size="sm">
            Version-case history can contain other compositions. It is not qualified evidence for
            this candidate.
          </Text>
          {history.isError ? (
            <Alert color="red">
              Run history unavailable.{' '}
              <Button
                variant="subtle"
                disabled={history.isFetching}
                onClick={() => void history.refetch()}
              >
                Retry Run history
              </Button>
            </Alert>
          ) : !history.isFetchedAfterMount || history.isFetching ? (
            <Text>Loading Run history…</Text>
          ) : (
            history.data?.runs.map((run) => (
              <CompositionRunLink key={run.run_id} runId={run.run_id}>
                {run.run_id} · {run.run_status}
              </CompositionRunLink>
            ))
          )}
        </Stack>
      )}
      {launch && rejected && (
        <Button
          variant="subtle"
          disabled={
            busy ||
            (!compact &&
              (!showHistory ||
                !history.isFetchedAfterMount ||
                !history.isSuccess ||
                history.isFetching))
          }
          onClick={() => {
            retainLaunch(storageKey, null);
            setLaunch(null);
            setPlan(null);
            setRejected(false);
            void source.refetch();
          }}
        >
          {compact
            ? 'Clear rejected test request'
            : 'Clear rejected request after reviewing Run history'}
        </Button>
      )}
      <Group>
        {!compact && (
          <Button
            variant={!plan && !launch ? 'filled' : 'default'}
            disabled={
              busy ||
              Boolean(launch) ||
              source.isError ||
              !source.isFetchedAfterMount ||
              !source.data?.latest_version_id ||
              (declaration.kind === 'comparison' && !producer.trim())
            }
            onClick={() => void prepare()}
          >
            Prepare exact test
          </Button>
        )}
        {compact && !launch && (expired || (error && !plan)) && (
          <Button
            variant="default"
            disabled={busy || source.isError || !source.isFetchedAfterMount}
            onClick={() => {
              hasPreparedCompactPlan.current = false;
              setPlan(null);
              setError('');
              void prepare();
            }}
          >
            {expired ? 'Prepare a fresh test' : 'Retry preparing test'}
          </Button>
        )}
        <Button
          variant={plan || launch ? 'filled' : 'default'}
          disabled={
            busy ||
            source.isError ||
            !source.data ||
            (!launch && (!plan?.eligible || !plan.plan_token || expired))
          }
          onClick={() => void run()}
        >
          {launch
            ? 'Recover same test request'
            : compact
              ? 'Run safe test'
              : 'Confirm and run safe test'}
        </Button>
        <Button variant="subtle" onClick={onEvidenceChange}>
          {compact ? 'Refresh test status' : 'Refresh evidence'}
        </Button>
      </Group>
      {runId && <CompositionRunLink runId={runId}>Inspect created Run {runId}</CompositionRunLink>}
    </Stack>
  );
}
